import type { RuntimeConfig } from '../auth/oauth';
import { microsoftLogoutUrl } from '../auth/session';

/**
 * What actually happened, which depends on how this screen was reached.
 *
 * With a logout URI registered for the console, the browser arrives here from Cognito's /logout,
 * which cleared its session cookie on the way. Without one, sign-out was local: the tokens are gone
 * and the refresh token revoked, but that cookie survives and this browser can still get a new
 * console session with no password prompt.
 *
 * The distinction is the whole point of the screen. Saying "you have been signed out" in the second
 * case would not be true, and saying the shared-workstation warning in the first would train people
 * to ignore it.
 */
export function SignedOut({ config }: { config: RuntimeConfig }) {
  const brokerSessionCleared = !!config.logoutUri;

  return (
    <div className="centered">
      <h1>Signed out of this console</h1>
      <p>
        Your tokens have been discarded and the refresh token revoked.
        {brokerSessionCleared && ' Your broker session has ended.'}
      </p>

      {!brokerSessionCleared && (
        <div className="callout callout-warn">
          <strong>Your broker session is still active.</strong> This console has no logout URI
          registered, so its sign-out could not clear the session cookie on{' '}
          <code>{config.domainName}</code>. This browser can still get a new console session without
          a password prompt.
          <p className="hint">
            An operator can fix this with{' '}
            <code>npm run broker -- app:update {'<admin app>'} --logout-uri {'<this URL>'}</code>.
          </p>
        </div>
      )}

      <div className="callout callout-warn">
        <strong>Your Microsoft session is still active.</strong> This did not sign you out of
        Microsoft, so signing back in here may not prompt for a password until that session expires.
        <p>
          On a shared workstation,{' '}
          <a href={microsoftLogoutUrl(config)} rel="noreferrer">
            also sign out of Microsoft
          </a>
          .
        </p>
      </div>

      <p>
        <a href="/">Sign back in</a>
      </p>
    </div>
  );
}
